import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Leaf, Plus, Pencil, Trash2, FileSpreadsheet, FileText, Eye, CheckCircle2,
} from "lucide-react";
import { api, downloadExport, fileViewUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge2, statusColor, validityFromDate } from "@/components/ComplianceBadge";
import { FileDropzone } from "@/components/FileDropzone";
import { IsoAiImport } from "@/components/IsoAiImport";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS = ["Pendente", "Em Andamento", "Concluído", "N/A"];
const CATEGORIES = ["Documento", "Política", "Procedimento", "Registro", "Licença", "Laudo", "Treinamento", "Equipamento", "Recurso"];
const CAT_COLOR = { Política: "blue", Procedimento: "blue", Documento: "slate", Registro: "slate", Licença: "green", Laudo: "yellow", Treinamento: "blue", Equipamento: "slate", Recurso: "slate" };

export default function ISO14001() {
  const { search } = useOutletContext() || {};
  const { canEdit, canManage } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/iso14001");
      data.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setRows(data);
    } catch { toast.error("Falha ao carregar"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ clause: "", section: "6. Planejamento", title: "", category: "Documento", status: "Pendente", responsible: "", due_date: "", notes: "", order: 999 }); setEditing(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEditing(r); setOpen(true); };
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const quickStatus = async (row, status) => {
    setRows((p) => p.map((r) => r.id === row.id ? { ...r, status } : r));
    try { await api.put(`/iso14001/${row.id}`, { ...row, status }); } catch { toast.error("Falha ao atualizar status"); load(); }
  };

  const save = async () => {
    if (!form.title) { toast.error("Informe o título do item"); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/iso14001/${editing.id}`, form); toast.success("Item atualizado"); }
      else { await api.post("/iso14001", form); toast.success("Item criado"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/iso14001/${deleteId}`); toast.success("Removido"); setDeleteId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(q)));
  }, [rows, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((r) => { (g[r.section] = g[r.section] || []).push(r); });
    return g;
  }, [filtered]);

  const stats = useMemo(() => {
    const valid = rows.filter((r) => r.status !== "N/A");
    const done = valid.filter((r) => r.status === "Concluído").length;
    return { total: valid.length, done, pct: valid.length ? Math.round((done / valid.length) * 100) : 0 };
  }, [rows]);

  const sectionPct = (items) => {
    const valid = items.filter((r) => r.status !== "N/A");
    const done = valid.filter((r) => r.status === "Concluído").length;
    return valid.length ? Math.round((done / valid.length) * 100) : 0;
  };

  return (
    <div data-testid="iso14001-page">
      <PageHeader icon={Leaf} title="Gestão ISO 14001 — SGA" subtitle="Checklist de implantação do Sistema de Gestão Ambiental (cláusulas 4 a 10)">
        <Button variant="outline" size="sm" onClick={() => downloadExport("iso14001", "excel")} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="iso-export-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
        <Button variant="outline" size="sm" onClick={() => downloadExport("iso14001", "pdf")} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="iso-export-pdf"><FileText className="mr-1.5 h-4 w-4" /> PDF</Button>
        {canEdit && <IsoAiImport onDone={load} />}
        {canEdit && <Button onClick={openNew} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="iso-new-btn"><Plus className="mr-1.5 h-4 w-4" /> Novo item</Button>}
      </PageHeader>

      {/* Overall progress */}
      <div className="mb-6 rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500 text-white"><CheckCircle2 className="h-5 w-5" /></div>
            <div>
              <p className="text-sm font-semibold text-[#134074]">Progresso da implantação</p>
              <p className="text-xs text-slate-400">{stats.done} de {stats.total} itens concluídos</p>
            </div>
          </div>
          <span className="text-3xl font-extrabold text-[#0B2545]">{stats.pct}%</span>
        </div>
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all" style={{ width: `${stats.pct}%` }} />
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Carregando...</div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([section, items]) => (
            <div key={section} className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-[#E2E8F0] bg-slate-50 px-4 py-3">
                <h3 className="text-sm font-bold text-[#0B2545]">{section}</h3>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-emerald-500" style={{ width: `${sectionPct(items)}%` }} /></div>
                  <span className="text-xs font-semibold text-slate-500">{sectionPct(items)}%</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {items.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid="iso-row">
                        <td className="whitespace-nowrap px-4 py-3 align-top">{r.clause ? <Badge2 color="blue">{r.clause}</Badge2> : <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{r.title}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Badge2 color={CAT_COLOR[r.category] || "slate"}>{r.category}</Badge2>
                            {r.responsible && <span className="text-xs text-slate-400">Resp.: {r.responsible}</span>}
                            {r.due_date && <Badge2 color={validityFromDate(r.due_date).color}>Prazo: {r.due_date}</Badge2>}
                            {r.file_id && <a href={fileViewUrl(r.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#134074] hover:underline"><Eye className="h-3 w-3" />{r.file_name || "arquivo"}</a>}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 align-top">
                          {canEdit ? (
                            <Select value={r.status} onValueChange={(v) => quickStatus(r, v)}>
                              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`iso-status-${r.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : <Badge2 color={statusColor(r.status)}>{r.status}</Badge2>}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right align-top">
                          {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`iso-edit-${r.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                          {canManage && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(r.id)} data-testid={`iso-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]" data-testid="iso-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">{editing ? "Editar" : "Novo"} item — ISO 14001</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <div><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Requisito (cláusula)</Label><Input value={form.clause || ""} onChange={(e) => setF("clause", e.target.value)} placeholder="ex: 6.1.2" data-testid="iso-field-clause" /></div>
            <div><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Seção</Label>
              <Select value={form.section} onValueChange={(v) => setF("section", v)}><SelectTrigger data-testid="iso-field-section"><SelectValue /></SelectTrigger><SelectContent>{["4. Contexto da Organização", "5. Liderança", "6. Planejamento", "7. Suporte", "8. Operação", "9. Avaliação de Desempenho", "10. Melhoria", "Documentos Importados"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Item / documento *</Label><Input value={form.title || ""} onChange={(e) => setF("title", e.target.value)} data-testid="iso-field-title" /></div>
            <div><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Tipo</Label>
              <Select value={form.category} onValueChange={(v) => setF("category", v)}><SelectTrigger data-testid="iso-field-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => setF("status", v)}><SelectTrigger data-testid="iso-field-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Responsável</Label><Input value={form.responsible || ""} onChange={(e) => setF("responsible", e.target.value)} /></div>
            <div><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Prazo</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setF("due_date", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Observações</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setF("notes", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block text-xs font-semibold text-slate-600">Evidência / documento</Label>
              <FileDropzone value={form.file_id} valueName={form.file_name} onUploaded={({ id, name }) => setForm((p) => ({ ...p, file_id: id, file_name: name }))} testid="iso-upload" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="iso-save-btn">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remover item</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
